import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import NumericInput from "@components/NumericInput";
import {
  AdminDeviceDetail,
  DeviceAnalyticsSettings,
  AnalyticsBaseParams,
  PotatoParams,
  PotatoParamsConfig,
  PotatoParamsChange,
  getDeviceAnalyticsSettings,
  updateDeviceAnalyticsSettings,
  getDeviceWidthSegments,
  setDeviceWidthSegments,
  DEFAULT_ANALYTICS_SETTINGS,
} from "@services/adminService";
import { LengthUnit } from "@services/demosService";
import WidthHistoryEditor, {
  type WidthRow,
} from "@components/WidthHistoryEditor";

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const Card = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: 100px;
`;

const Message = styled.div<{ error?: boolean }>`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ error }) => (error ? "#e74c3c" : "#68ad18")};
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Select = styled.select`
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
  width: 100%;
  box-sizing: border-box;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateTimeInput = styled.input`
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  width: 100%;
  box-sizing: border-box;
`;

const ChangeBlock = styled.div`
  border: 1px solid #ebeeef;
  border-radius: 6px;
  padding: 16px;
  margin-top: 12px;
  background: #ffffff;
`;

const ChangeBlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 12px;
  color: #494c5b;
  font-weight: 600;
`;


const RemoveBtn = styled.span`
  font-size: 12px;
  color: #e74c3c;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const AddChangeBtn = styled(Button)`
  width: auto;
  margin-top: 12px;
`;

const HintText = styled.span`
  font-size: 10px;
  color: #b0b8bb;
  margin-left: 4px;
  font-weight: 400;
`;

const DynamicTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #4a90d9;
  background: #f0f7ff;
`;

const DynamicToggle = styled.button`
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid #c3d9f5;
  background: #e8f2fd;
  color: #4a90d9;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  white-space: nowrap;
  &:hover { background: #d0e6fb; }
`;

const BaselineRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-end;
`;


const DEVICE_TYPES: Record<number, string> = {
  1: "RETROFIT",
  2: "CAN",
  3: "POTATO",
};

function isRetrofitOrCan(deviceType: number): boolean {
  return deviceType === 1 || deviceType === 2;
}

function isPotato(deviceType: number): boolean {
  return deviceType === 3;
}

interface Props {
  esn: string;
  detail: AdminDeviceDetail;
}

const DeviceAnalyticsSettingsSubTab: React.FC<Props> = ({ esn, detail }) => {
  const deviceType = detail.device.deviceType;
  const [settings, setSettings] = useState<DeviceAnalyticsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  // Header-width history. Same list the device owner edits in the app; admin
  // reaches it for any device, which is the correction path for a device that
  // was auto-provisioned at the 6.7 m default it never actually had.
  const [widthRows, setWidthRows] = useState<WidthRow[] | null>(null);
  const [widthSaving, setWidthSaving] = useState(false);

  const defaults = DEFAULT_ANALYTICS_SETTINGS;

  // After a failed load the form falls back to built-in defaults; saving them
  // would overwrite this device's real overrides — so no save.
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDeviceAnalyticsSettings(esn);
      setSettings(data);
      setLoadFailed(false);
    } catch (e) {
      setSettings(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [esn]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const fetchWidthSegments = useCallback(async () => {
    try {
      const segments = await getDeviceWidthSegments(esn);
      setWidthRows(
        segments.map((sg) => ({
          effectiveFrom: sg.effectiveFrom,
          widthMeters: sg.widthMeters,
        }))
      );
    } catch (e) {
      setWidthRows([]);
    }
  }, [esn]);

  useEffect(() => {
    fetchWidthSegments();
  }, [fetchWidthSegments]);

  const saveWidthSegments = async () => {
    if (!widthRows || widthRows.length === 0) return;
    setWidthSaving(true);
    try {
      const saved = await setDeviceWidthSegments(esn, widthRows);
      setWidthRows(
        saved.map((sg) => ({
          effectiveFrom: sg.effectiveFrom,
          widthMeters: sg.widthMeters,
        }))
      );
      setMessage({ text: "Header width history saved", error: false });
    } catch (e: any) {
      setMessage({
        text: e?.message ?? "Failed to save header width history",
        error: true,
      });
    } finally {
      setWidthSaving(false);
    }
  };

  const getBaseParams = (): AnalyticsBaseParams => {
    if (isRetrofitOrCan(deviceType) && settings?.retrofit) return settings.retrofit;
    if (isPotato(deviceType) && settings?.potato) {
      const { params: _p, params_default: _d, ...base } = settings.potato as any;
      return base;
    }
    if (isRetrofitOrCan(deviceType)) return defaults.retrofit;
    const { params_default: _pd, ...potatoBase } = defaults.potato;
    return potatoBase;
  };

  const setBaseParams = (updates: Partial<AnalyticsBaseParams>) => {
    if (isRetrofitOrCan(deviceType)) {
      setSettings((prev) => ({
        ...prev,
        retrofit: { ...(prev?.retrofit ?? defaults.retrofit), ...updates },
      }));
    } else if (isPotato(deviceType)) {
      setSettings((prev) => {
        const { params_default, ...potatoBase } = defaults.potato;
        return {
          ...prev,
          potato: prev?.potato
            ? { ...prev.potato, ...updates }
            : {
                ...potatoBase,
                params: { default: defaults.potato.params_default, changes: [] },
                ...updates,
              },
        };
      });
    }
  };

  const getPotatoParamsConfig = (): PotatoParamsConfig => {
    const raw = settings?.potato?.params;
    const mergedDefault: PotatoParams = {
      ...defaults.potato.params_default,
      ...(raw?.default ?? {}),
    };
    const changesRaw = raw?.changes;
    return {
      default: mergedDefault,
      changes: Array.isArray(changesRaw) ? changesRaw : [],
    };
  };

  const setPotatoParamsConfig = (config: PotatoParamsConfig) => {
    setSettings((prev) => {
      const { params_default, ...potatoBase } = defaults.potato;
      const potato = prev?.potato ?? { ...potatoBase, params: undefined };
      return { ...prev, potato: { ...potato, params: config } };
    });
  };

  const handleSave = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const toSave: DeviceAnalyticsSettings = {};
      if (isRetrofitOrCan(deviceType) && settings?.retrofit) {
        toSave.retrofit = settings.retrofit;
      }
      if (isPotato(deviceType) && settings?.potato) {
        toSave.potato = settings.potato;
      }
      await updateDeviceAnalyticsSettings(esn, toSave);
      setMessage({ text: "Saved successfully", error: false });
      fetchSettings();
    } catch (e: any) {
      setMessage({ text: e.message || "Failed to save", error: true });
    } finally {
      setSaving(false);
    }
  };

  const addChange = () => {
    const config = getPotatoParamsConfig();
    if (!config) return;
    const now = new Date();
    const startDate = now.toISOString().slice(0, 16).replace("T", " ");
    const newChange: PotatoParamsChange = {
      start_date: startDate,
      ...config.default,
    };
    setPotatoParamsConfig({
      ...config,
      changes: [...config.changes, newChange],
    });
  };

  const removeChange = (index: number) => {
    const config = getPotatoParamsConfig();
    if (!config) return;
    setPotatoParamsConfig({
      ...config,
      changes: config.changes.filter((_, i) => i !== index),
    });
  };

  const updateChange = (index: number, updates: Partial<PotatoParamsChange>) => {
    const config = getPotatoParamsConfig();
    if (!config) return;
    const newChanges = [...config.changes];
    newChanges[index] = { ...newChanges[index], ...updates };
    setPotatoParamsConfig({ ...config, changes: newChanges });
  };

  const updateDefaultParams = (updates: Partial<PotatoParams>) => {
    const config = getPotatoParamsConfig();
    if (!config) return;
    setPotatoParamsConfig({
      ...config,
      default: { ...config.default, ...updates },
    });
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  const baseParams = getBaseParams();
  const potatoConfig = getPotatoParamsConfig();

  return (
    <div>
      <Section>
        <SectionTitle>Device type: {DEVICE_TYPES[deviceType] ?? deviceType}</SectionTitle>
      </Section>

      {/* Base params (shared) */}
      <Card>
        <SectionTitle>Base parameters</SectionTitle>
        <FormGrid>
          <FieldWrap>
            <Text size={12} color="#899093">
              Min speed (km/h)
            </Text>
            <NumericInput
              value={baseParams.min_speed}
              min={0}
              onChange={(v) => setBaseParams({ min_speed: v })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Max speed (km/h)
            </Text>
            <NumericInput
              value={baseParams.max_speed}
              min={0}
              onChange={(v) => setBaseParams({ max_speed: v })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delay (s)
            </Text>
            <NumericInput
              value={baseParams.delay_s}
              onChange={(v) => setBaseParams({ delay_s: v })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delay tolerance
            </Text>
            <NumericInput
              value={baseParams.delay_tolerance}
              onChange={(v) => setBaseParams({ delay_tolerance: v })}
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Lateral side
            </Text>
            <Select
              value={baseParams.lateral_side}
              onChange={(e) =>
                setBaseParams({
                  lateral_side: e.target.value as "R" | "L",
                })
              }
            >
              <option value="R">R</option>
              <option value="L">L</option>
            </Select>
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Lateral shift (m)
            </Text>
            <NumericInput
              value={baseParams.lateral_shift_m}
              onChange={(v) => setBaseParams({ lateral_shift_m: v })}
            />
          </FieldWrap>
        </FormGrid>
      </Card>

      {/* Header width over time — applies to every device type: harvested area
          is measured as distance x the width fitted on the day, and the yield
          coefficient is anchored to that same width. */}
      {widthRows != null && (
        <Card>
          <SectionTitle>Header width over time</SectionTitle>
          <Text size={12} color="#899093" style={{ marginBottom: 12 }}>
            The width in force at a timestamp is the most recent line starting at
            or before it; the first line governs everything earlier. This is the
            same list the device owner sees in the app. Editing a line
            recalculates harvested area and yield for the days it covers, and
            rescales the width-anchored calibration coefficient from that point.
          </Text>
          <WidthHistoryEditor
            rows={widthRows}
            onChange={setWidthRows}
            lengthUnit={LengthUnit.Meter}
            unitLabel="m"
            disabled={widthSaving}
            labels={{
              fromTheStart: "From the start",
              changedOn: "Changed on",
              addNew: "+ Add new header width (from now)",
              addEarlier: "+ Add earlier width",
              remove: "Remove",
              restatesWarning:
                "Changing or removing a line recalculates area and yield for the days it covers.",
            }}
          />
          <Actions>
            <ActionButton onClick={saveWidthSegments} disabled={widthSaving}>
              {widthSaving ? "Saving..." : "Save width history"}
            </ActionButton>
          </Actions>
        </Card>
      )}

      {/* Potato-specific params */}
      {isPotato(deviceType) && potatoConfig && (
        <Card>
          <SectionTitle>Potato parameters</SectionTitle>
          <Text size={12} color="#899093" style={{ marginBottom: 12 }}>
            Default values and time-based overrides. When requesting parameters for a
            datetime, the most recent change with start_date &le; requested time applies.
          </Text>

          <SectionTitle style={{ marginTop: 16 }}>Primary parameters</SectionTitle>
          <FormGrid>
            <FieldWrap>
              <Text size={12} color="#899093">Parameter</Text>
              <Select
                value={potatoConfig.default.parameter}
                onChange={(e) =>
                  updateDefaultParams({
                    parameter: e.target.value as "sum" | "value" | "freq",
                  })
                }
              >
                <option value="sum">sum</option>
                <option value="value">value</option>
                <option value="freq">freq</option>
              </Select>
            </FieldWrap>

            <FieldWrap>
              <Text size={12} color="#899093">
                Baseline
                <HintText>for value/freq — used in |sensor − baseline|</HintText>
              </Text>
              {potatoConfig.default.baseline === "dynamic" ? (
                <DynamicTag>
                  dynamic
                  <DynamicToggle onClick={() => updateDefaultParams({ baseline: 0 })}>
                    ✕ reset
                  </DynamicToggle>
                </DynamicTag>
              ) : (
                <BaselineRow>
                  <NumericInput
                    value={typeof potatoConfig.default.baseline === "number" ? potatoConfig.default.baseline : 0}
                    onChange={(v) => updateDefaultParams({ baseline: v })}
                  />
                  <DynamicToggle onClick={() => updateDefaultParams({ baseline: "dynamic" })}>
                    dynamic
                  </DynamicToggle>
                </BaselineRow>
              )}
            </FieldWrap>
            <FieldWrap>
              <Text size={12} color="#899093">
                Baseline value
                <HintText>for sum — used in |value − baseline_value|</HintText>
              </Text>
              <NumericInput
                value={potatoConfig.default.baseline_value}
                onChange={(v) => updateDefaultParams({ baseline_value: v })}
              />
            </FieldWrap>
            <FieldWrap>
              <Text size={12} color="#899093">
                Baseline freq
                <HintText>for sum — used in |freq − baseline_freq|</HintText>
              </Text>
              <NumericInput
                value={potatoConfig.default.baseline_freq}
                onChange={(v) => updateDefaultParams({ baseline_freq: v })}
              />
            </FieldWrap>

            <FieldWrap>
              <Text size={12} color="#899093">Reaper (-1 to 100)</Text>
              <NumericInput
                value={potatoConfig.default.reaper}
                min={-1}
                max={100}
                onChange={(v) => updateDefaultParams({ reaper: Math.round(v) })}
              />
            </FieldWrap>
            <FieldWrap>
              <Text size={12} color="#899093">Delta min</Text>
              <NumericInput
                value={potatoConfig.default.delta_min}
                onChange={(v) => updateDefaultParams({ delta_min: Math.round(v) })}
              />
            </FieldWrap>
            <FieldWrap>
              <Text size={12} color="#899093">Delta max</Text>
              <NumericInput
                value={potatoConfig.default.delta_max}
                onChange={(v) => updateDefaultParams({ delta_max: Math.round(v) })}
              />
            </FieldWrap>
          </FormGrid>

          <SectionTitle style={{ marginTop: 20 }}>Time-based changes</SectionTitle>
          {potatoConfig.changes.map((ch, i) => (
            <ChangeBlock key={i}>
              <ChangeBlockHeader>
                <span>Change #{i + 1}</span>
                <RemoveBtn onClick={() => removeChange(i)}>Remove</RemoveBtn>
              </ChangeBlockHeader>
              <FormGrid>
                <FieldWrap>
                  <Text size={12} color="#899093">Start date (UTC)</Text>
                  <DateTimeInput
                    type="datetime-local"
                    value={ch.start_date.replace(" ", "T")}
                    onChange={(e) => {
                      const v = e.target.value.replace("T", " ");
                      updateChange(i, { start_date: v });
                    }}
                  />
                </FieldWrap>
                <FieldWrap>
                  <Text size={12} color="#899093">Parameter</Text>
                  <Select
                    value={ch.parameter}
                    onChange={(e) =>
                      updateChange(i, {
                        parameter: e.target.value as "sum" | "value" | "freq",
                      })
                    }
                  >
                    <option value="sum">sum</option>
                    <option value="value">value</option>
                    <option value="freq">freq</option>
                  </Select>
                </FieldWrap>
                <FieldWrap>
                  <Text size={12} color="#899093">
                    Baseline
                    <HintText>for value/freq</HintText>
                  </Text>
                  {ch.baseline === "dynamic" ? (
                    <DynamicTag>
                      dynamic
                      <DynamicToggle onClick={() => updateChange(i, { baseline: 0 })}>
                        ✕ reset
                      </DynamicToggle>
                    </DynamicTag>
                  ) : (
                    <BaselineRow>
                      <NumericInput
                        value={typeof ch.baseline === "number" ? ch.baseline : 0}
                        onChange={(v) => updateChange(i, { baseline: v })}
                      />
                      <DynamicToggle onClick={() => updateChange(i, { baseline: "dynamic" })}>
                        dynamic
                      </DynamicToggle>
                    </BaselineRow>
                  )}
                </FieldWrap>
                <FieldWrap>
                  <Text size={12} color="#899093">
                    Baseline value
                    <HintText>for sum</HintText>
                  </Text>
                  <NumericInput
                    value={ch.baseline_value}
                    onChange={(v) => updateChange(i, { baseline_value: v })}
                  />
                </FieldWrap>
                <FieldWrap>
                  <Text size={12} color="#899093">
                    Baseline freq
                    <HintText>for sum</HintText>
                  </Text>
                  <NumericInput
                    value={ch.baseline_freq}
                    onChange={(v) => updateChange(i, { baseline_freq: v })}
                  />
                </FieldWrap>
                <FieldWrap>
                  <Text size={12} color="#899093">Reaper (-1 to 100)</Text>
                  <NumericInput
                    value={ch.reaper}
                    min={-1}
                    max={100}
                    onChange={(v) => updateChange(i, { reaper: Math.round(v) })}
                  />
                </FieldWrap>
                <FieldWrap>
                  <Text size={12} color="#899093">Delta min</Text>
                  <NumericInput
                    value={ch.delta_min}
                    onChange={(v) => updateChange(i, { delta_min: Math.round(v) })}
                  />
                </FieldWrap>
                <FieldWrap>
                  <Text size={12} color="#899093">Delta max</Text>
                  <NumericInput
                    value={ch.delta_max}
                    onChange={(v) => updateChange(i, { delta_max: Math.round(v) })}
                  />
                </FieldWrap>
              </FormGrid>
            </ChangeBlock>
          ))}
          <AddChangeBtn kind="outline" onClick={addChange}>
            Add time-based change
          </AddChangeBtn>
        </Card>
      )}

      <Actions>
        <ActionButton onClick={handleSave} disabled={saving || loadFailed}>
          {saving ? "Saving..." : "Save"}
        </ActionButton>
      </Actions>
      {message && <Message error={message.error}>{message.text}</Message>}
    </div>
  );
};

export default DeviceAnalyticsSettingsSubTab;
